import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const STRINGS = {
  interview: { en: "Guided interview", ur: "رہنمائی کے ساتھ سوالات" },
  fullForm: { en: "Use the full form instead", ur: "مکمل فارم استعمال کریں" },
  situation: { en: "Full form: situation", ur: "مکمل فارم: صورتحال" },
  details: { en: "Full form: details", ur: "مکمل فارم: تفصیلات" },
  letters: { en: "Letters", ur: "خطوط" },
  checks: { en: "Checks", ur: "جانچ" },
  file: { en: "Print file", ur: "پرنٹ فائل" },
  continue: { en: "Continue", ur: "آگے بڑھیں" },
  skip: { en: "Skip", ur: "چھوڑ دیں" },
  yes: { en: "Yes", ur: "ہاں" },
  no: { en: "No", ur: "نہیں" },
  edit: { en: "Edit", ur: "تبدیل کریں" },
  add: { en: "Add this entry", ur: "یہ اندراج شامل کریں" },
  remove: { en: "Remove", ur: "ہٹائیں" },
  question: { en: "Question", ur: "سوال" },
  of: { en: "of", ur: "از" },
  earlier: { en: "Show earlier answers", ur: "پچھلے جوابات دکھائیں" },
  hide: { en: "Hide earlier answers", ur: "پچھلے جوابات چھپائیں" },
  greeting: { en: "Assalam o Alaikum", ur: "السلام علیکم" },
  greetSub: { en: "Answer one question at a time. Your answers fill every official form for you.", ur: "ایک وقت میں ایک سوال کا جواب دیں۔ آپ کے جوابات سے تمام سرکاری فارم خود بخود بھر جائیں گے۔" },
  inUrdu: { en: "In Urdu", ur: "اردو میں" },
  done: { en: "All questions answered", ur: "تمام سوالات مکمل ہو گئے" },
  doneSub: { en: "Next, write your letters and run the checks before printing your file.", ur: "اب اپنے خطوط لکھیں اور فائل پرنٹ کرنے سے پہلے جانچ چلائیں۔" },
  saving: { en: "Saving…", ur: "محفوظ ہو رہا ہے…" },
  notes: { en: "Worth fixing now", ur: "ابھی درست کرنا بہتر ہے" },
  pickDate: { en: "Choose a date", ur: "تاریخ منتخب کریں" },
  unavailable: { en: "The guided interview is not available for this visa yet. Use the full form.", ur: "اس ویزا کے لیے رہنمائی والے سوالات ابھی دستیاب نہیں۔ مکمل فارم استعمال کریں۔" },
};

const Ctx = createContext(null);

function detectPakistan() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const lang = navigator.language || "";
    const q = new URLSearchParams(window.location.search).get("pk");
    return tz === "Asia/Karachi" || /^ur|^en-PK/i.test(lang) || q === "1" || localStorage.getItem("hv_pk") === "1";
  } catch (e) { return false; }
}

export function LocaleProvider({ children }) {
  const [pk, setPk] = useState(detectPakistan);
  const [lang, setLangState] = useState(() => localStorage.getItem("hv_lang") || ((navigator.language || "").startsWith("ur") ? "ur" : "en"));

  useEffect(() => {
    api.get("/locale").then((r) => { if (r.data.is_pk) { localStorage.setItem("hv_pk", "1"); setPk(true); } }).catch(() => {});
  }, []);

  const setLang = useCallback((l) => { localStorage.setItem("hv_lang", l); setLangState(l); }, []);
  const active = pk ? lang : "en";
  const value = useMemo(() => ({
    pk, lang: active, setLang, rtl: active === "ur",
    t: (k) => (STRINGS[k] ? STRINGS[k][active] : k),
    pick: (obj) => (obj ? obj[active] || obj.en : ""),
  }), [pk, active, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useLocale = () => useContext(Ctx);
